import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'store/hooks';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';

import {
    BudgetExpenseOrIncome,
    STALE_TIME,
    BudgetPhases,
} from 'business/constants';
import {
    BudgetProposalT,
    CellRendererParams,
} from 'routes/BudgetModule/config/types';
import { BUDGET_LINE_ID } from 'routes/BudgetModule/config/constants';
import InscriptionsGrid from 'routes/BudgetModule/components/shared/InscriptionsGrid/InscriptionsGrid';
import WithLoader from 'ui/WithLoader/WithLoader';
import {
    getInscriptionsGridColumns,
    getInscriptionsGridColumnsWithPhase,
} from 'routes/BudgetModule/components/shared/InscriptionsGrid/gridHelpers';

import { swap } from 'routes/BudgetModule/helpers/columnOrder';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { useAddInscription } from '../hooks/inscriptions';
import {
    applyInscriptionsFilter,
    sortInscriptionsVotedFirst,
} from '../gridHelpers/helpers';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { getClientBind } from 'store/APIClient';
import { ExpenseIncomeImputationDataT } from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/hooks/expenseIncomeImputations';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';

type Props = CellRendererParams<ExpenseIncomeImputationDataT> & {
    budgetProposal: BudgetProposalT;
    budgetProposals: BudgetProposalT[];
    budgetLineId: number;
    canAddInscription: boolean;
    expenseOrIncome: BudgetExpenseOrIncome;
    filterInscriptions: string;
    projects: FlowAnyObject[];
    queryCacheKey: InscriptionQueryKey;
};

const StyButtonWrapper = styled(Box, {
    name: 'StyButtonWrapper',
})(({ theme }) => ({
    width: '100%',
    background: theme.palette.lightGrey,
}));

const StyButtonIcon = styled(AddIcon, {
    name: 'StyButtonIcon',
})(({ theme }) => ({
    color: 'white',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '10px',
    fontSize: '16px',
}));

const StyButtonText = styled(Typography, {
    name: 'StyButtonText',
})(({ theme }) => ({
    fontSize: '11.5px',
    color: theme.palette.primary.main,
    margin: '0 0 0 6px',
}));

export default function DetailCellRenderer({
    budgetProposal,
    budgetProposals,
    budgetLineId,
    canAddInscription,
    api,
    expenseOrIncome,
    filterInscriptions,
    projects,
    queryCacheKey,
    data: budgetLine,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const statusesSettings = useContext(StatusSettingsContext);

    const { data: inscriptionsData = { data: [] } } = useQuery<{
        data: InscriptionT[];
    }>({
        queryKey: queryCacheKey,
        initialData: { data: [] },
        staleTime: STALE_TIME, // no need to re-fetch it each time we expand a row
    });

    const allInscriptions = inscriptionsData.data;

    let inscriptions = (
        filterInscriptions
            ? applyInscriptionsFilter(
                  allInscriptions,
                  filterInscriptions,
                  projects,
              )
            : allInscriptions
    ).filter((inscription) => inscription[BUDGET_LINE_ID] === budgetLineId);

    // We sort the inscriptions so that the voted ones are above
    if (budgetProposal.phase === BudgetPhases.SUPPLEMENTARY_BUDGET) {
        inscriptions = inscriptions.sort(
            sortInscriptionsVotedFirst(statusesSettings),
        );
    }

    React.useEffect(() => {
        // Force re-render to update the grid because, when inscriptions change,
        // the custom aggregations need to be re-computed (aggStatus & aggAmount)
        api.refreshCells();
    }, [inscriptions]);

    const columns = [
        ...getInscriptionsGridColumns(
            t,
            budgetProposals,
            statusesSettings,
            null,
            false,
            projects,
            budgetProposal.exercice,
        ),
        ...getInscriptionsGridColumnsWithPhase(
            budgetProposal.phase,
            t,
            statusesSettings,
        ),
    ];
    const indexOfOriginalInscription = columns
        .map((col) => col.field)
        .indexOf('originalInscription');

    const addInscriptionMutation = useAddInscription(
        (error) => {
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
        },
        (message) => {
            dispatch.vision.notifyUser({
                message,
                snackType: 'success',
            });
        },
        queryClient,
        canAddInscription,
        budgetProposal.phase,
        { budgetLineId },
        queryCacheKey,
    );

    const hasBudgetLineForCurrentExercice = budgetLineId > 0;

    // (explo-AMP Shortcut ID : 28345) momentary trick to rebuild an "inscriptionsWithMetadata"
    // sadly necessary because in some occasions, we need inscription.budgetProposalPhase
    const inscriptionsWithMetadata = inscriptions.map(
        (inscription: InscriptionT) => ({
            inscription,
        }),
    );
    // We might want to implement a generic mechanism for budget lines that are not budgetable
    // For now only AMP needs it.
    const isAmpMetropole = getClientBind() === 'ampmetropole';
    const canAddInscriptionForCurrentExercice = isAmpMetropole
        ? hasBudgetLineForCurrentExercice &&
          budgetLine?.isAmpmetropoleBudgetable
        : hasBudgetLineForCurrentExercice;
    return (
        <Box sx={{ marginLeft: '2.5rem' }}>
            <InscriptionsGrid
                inscriptions={inscriptionsWithMetadata}
                nbDisplayedInscriptions={inscriptionsWithMetadata.length - 2}
                // As the original Inscription is a custom column that we use specifically here
                // we have to set its position in the grid.
                // TODO: Use a defined order
                columns={swap(columns, indexOfOriginalInscription, 2)}
                budgetProposals={budgetProposals}
                expenseOrIncome={expenseOrIncome}
                canAddInscriptions={canAddInscription}
                suppressHorizontalScroll
                suppressNoRowsOverlay
                queryCacheKey={queryCacheKey}
            />
            {canAddInscription ? (
                <StyButtonWrapper>
                    <WithLoader
                        isLoading={addInscriptionMutation.isLoading}
                        color="secondary"
                    >
                        <Grid container alignItems="center" className="subGrid">
                            {!addInscriptionMutation.isLoading &&
                                canAddInscriptionForCurrentExercice && (
                                    <Button
                                        sx={{ marginLeft: '24px' }}
                                        variant="text"
                                        size="small"
                                        onClick={() => {
                                            addInscriptionMutation.mutate();
                                        }}
                                    >
                                        <StyButtonIcon fontSize="small" />
                                        <StyButtonText variant="body1SemiBold">
                                            {t('budgetProposal.addInscription')}
                                        </StyButtonText>
                                    </Button>
                                )}
                            {!canAddInscriptionForCurrentExercice && (
                                <Typography
                                    style={{
                                        paddingLeft: '2rem',
                                        paddingTop: '1rem',
                                        fontSize: '12px',
                                    }}
                                >
                                    {t('budgetProposal.noBudgetLine', {
                                        year: budgetProposal.exercice,
                                    })}
                                </Typography>
                            )}
                        </Grid>
                    </WithLoader>
                </StyButtonWrapper>
            ) : null}
        </Box>
    );
}
